'use client';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import LayerSwapApiClient from '../lib/apiClients/layerSwapApiClient';
import { MAX_PERSISTED_SWAP_ROUTES, swapRouteHistoryRepository, } from '../lib/swapRouteHistoryDb';
const EMPTY_RECORDS = [];
const hydrationPromises = new Map();
export const useSwapRouteHistoryStore = create(() => ({
    records: EMPTY_RECORDS,
}));
export function getSwapRouteHistoryEnvironment() {
    const apiBase = (LayerSwapApiClient.apiBaseEndpoint || 'default').replace(/\/+$/, '');
    const apiKey = LayerSwapApiClient.apiKey || 'default';
    return `${apiBase}|${apiKey}`;
}
export function toPersistedSwapRoute(response, environment, overrides = {}) {
    const swap = response.swap;
    const sourceRoute = Object.prototype.hasOwnProperty.call(overrides, 'sourceRoute')
        ? overrides.sourceRoute ?? undefined
        : swap.source_exchange
            ? undefined
            : asRouteIdentity(swap.source_network?.name, swap.source_token?.symbol);
    const destinationRoute = overrides.destinationRoute
        ?? asRouteIdentity(swap.destination_network?.name, swap.destination_token?.symbol);
    if (!swap.id || !destinationRoute)
        return undefined;
    const parsedCreatedAt = Date.parse(swap.created_date);
    return {
        swapId: swap.id,
        environment,
        createdAt: Number.isFinite(parsedCreatedAt) ? parsedCreatedAt : Date.now(),
        sourceRoute,
        destinationRoute,
    };
}
export function mergeSwapRouteHistoryRecords(current, updates) {
    if (updates.length === 0)
        return current;
    const recordsById = new Map(current.map(record => [record.swapId, record]));
    for (const record of updates)
        recordsById.set(record.swapId, record);
    const merged = [...recordsById.values()]
        .sort((a, b) => b.createdAt - a.createdAt || a.swapId.localeCompare(b.swapId))
        .slice(0, MAX_PERSISTED_SWAP_ROUTES);
    if (merged.length === current.length && merged.every((record, index) => recordsEqual(record, current[index]))) {
        return current;
    }
    return merged;
}
export function buildSwapRouteRecency(records) {
    const sourceRoutes = new Map();
    const destinationRoutes = new Map();
    for (const record of records) {
        if (record.sourceRoute)
            setLatest(sourceRoutes, record.sourceRoute, record.createdAt);
        setLatest(destinationRoutes, record.destinationRoute, record.createdAt);
    }
    return { sourceRoutes, destinationRoutes };
}
export function getRouteRecency(recency, direction, network, token) {
    const index = direction === 'from' ? recency.sourceRoutes : recency.destinationRoutes;
    return index.get(routeKey(network, token)) ?? 0;
}
export function hydrateSwapRouteHistory(environment = getSwapRouteHistoryEnvironment(), repository = swapRouteHistoryRepository) {
    const state = useSwapRouteHistoryStore.getState();
    if (state.hydratedEnvironment === environment)
        return Promise.resolve();
    if (state.environment !== environment) {
        useSwapRouteHistoryStore.setState({
            environment,
            hydratedEnvironment: undefined,
            records: EMPTY_RECORDS,
        });
    }
    const inFlight = hydrationPromises.get(environment);
    if (inFlight)
        return inFlight;
    const promise = repository.getRecent(environment)
        .then(persistedRecords => {
        const current = useSwapRouteHistoryStore.getState();
        if (current.environment !== environment)
            return;
        // In-memory records may have been created while IndexedDB was
        // loading. Apply them last so hydration cannot overwrite them.
        const records = mergeSwapRouteHistoryRecords(persistedRecords, current.records);
        useSwapRouteHistoryStore.setState({
            records,
            hydratedEnvironment: environment,
        });
    })
        .catch(() => {
        const current = useSwapRouteHistoryStore.getState();
        if (current.environment === environment) {
            useSwapRouteHistoryStore.setState({ hydratedEnvironment: environment });
        }
    })
        .finally(() => {
        hydrationPromises.delete(environment);
    });
    hydrationPromises.set(environment, promise);
    return promise;
}
export function recordSwapRouteHistory(response, overrides, repository = swapRouteHistoryRepository) {
    return recordSwapRoutes([response], overrides ? [overrides] : undefined, repository);
}
export function recordFetchedSwapRouteHistory(responses, repository = swapRouteHistoryRepository) {
    return recordSwapRoutes(responses, undefined, repository);
}
export function useSwapRouteRecency() {
    const environment = getSwapRouteHistoryEnvironment();
    const records = useSwapRouteHistoryStore(state => state.environment === environment ? state.records : EMPTY_RECORDS);
    useEffect(() => {
        void hydrateSwapRouteHistory(environment);
    }, [environment]);
    return useMemo(() => buildSwapRouteRecency(records), [records]);
}
async function recordSwapRoutes(responses, overrides, repository) {
    if (responses.length === 0)
        return;
    const environment = getSwapRouteHistoryEnvironment();
    const records = responses
        .map((response, index) => toPersistedSwapRoute(response, environment, overrides?.[index]))
        .filter((record) => !!record);
    if (records.length === 0)
        return;
    const state = useSwapRouteHistoryStore.getState();
    const currentRecords = state.environment === environment ? state.records : EMPTY_RECORDS;
    const currentById = new Map(currentRecords.map(record => [record.swapId, record]));
    const changedRecords = records.filter(record => !recordsEqual(record, currentById.get(record.swapId)));
    if (state.environment === environment && changedRecords.length === 0)
        return;
    const mergedRecords = mergeSwapRouteHistoryRecords(currentRecords, changedRecords);
    useSwapRouteHistoryStore.setState({
        environment,
        hydratedEnvironment: state.environment === environment ? state.hydratedEnvironment : undefined,
        records: mergedRecords,
    });
    await repository.upsert(changedRecords);
}
function asRouteIdentity(network, token) {
    if (!network || !token)
        return undefined;
    return { network, token };
}
function routeKey(network, token) {
    return `${network}\u0000${token}`;
}
function setLatest(index, route, createdAt) {
    const key = routeKey(route.network, route.token);
    const previous = index.get(key) ?? 0;
    if (createdAt > previous)
        index.set(key, createdAt);
}
function recordsEqual(a, b) {
    return !!b
        && a.swapId === b.swapId
        && a.environment === b.environment
        && a.createdAt === b.createdAt
        && routesEqual(a.sourceRoute, b.sourceRoute)
        && routesEqual(a.destinationRoute, b.destinationRoute);
}
function routesEqual(a, b) {
    return a?.network === b?.network && a?.token === b?.token;
}
